import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { downloadPdf } from '../lib/download';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { StatusBadge } from '../components/common/StatusBadge';
import { 
  Plus, 
  Search, 
  FileText,
  Download,
  Eye,
  MoreVertical,
  Pencil,
  Trash2,
  ExternalLink,
  Send,
  CheckCircle,
  Lock,
  Receipt
} from 'lucide-react';
import { FactureEditDialog } from '../components/facturation/FactureEditDialog';
import { FactureVerrouActions, isFactureLocked } from '../components/facturation/FactureVerrouActions';
import { FiltrePeriode, estDansPeriode, BoutonTelechargerZip } from '../components/facturation/TelechargementGroupe';
import { NumeroDocumentField } from '../components/facturation/NumeroDocumentField';
import { TriHead, cycleTri, trierDocuments, STATUTS_DOC } from '../components/facturation/FacturationTri';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOCUMENT_TYPES = [
  { value: 'devis', label: 'Devis' },
  { value: 'convention', label: 'Convention' },
  { value: 'facture', label: 'Facture' }
];

const STATUTS = [
  { value: 'brouillon', label: 'Brouillon' },
  { value: 'envoye', label: 'Envoyé' },
  { value: 'signe', label: 'Signé' },
  { value: 'paye', label: 'Payé' },
  { value: 'annule', label: 'Annulé' }
];

export const FacturationPage = () => {
  const [documents, setDocuments] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [tri, setTri] = useState({ col: null, dir: 'asc' });
  const [filterStatut, setFilterStatut] = useState('all');
  const [filterDestinataire, setFilterDestinataire] = useState('all');
  const [periode, setPeriode] = useState({ du: '', au: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFacture, setEditingFacture] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [editingDocument, setEditingDocument] = useState(null);
  const [numeroValide, setNumeroValide] = useState(true);
  
  const [formData, setFormData] = useState({
    dossier_id: '',
    type_document: 'devis',
    numero: '',
    montant_ht: 0,
    tva: 20,
    statut: 'brouillon',
    date_emission: '',
    date_echeance: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [documentsRes, dossiersRes] = await Promise.all([
        axios.get(`${API_URL}/documents/all`),
        axios.get(`${API_URL}/dossiers`)
      ]);
      setDocuments(documentsRes.data);
      setDossiers(dossiersRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (document = null) => {
    if (document) {
      setEditingDocument(document);
      setFormData({
        dossier_id: document.dossier_id,
        type_document: document.type_document,
        numero: document.numero,
        montant_ht: document.montant_ht,
        tva: document.tva,
        statut: document.statut,
        date_emission: document.date_emission || '',
        date_echeance: document.date_echeance || '',
        notes: document.notes || ''
      });
    } else {
      setEditingDocument(null);
      setFormData({
        dossier_id: '',
        type_document: 'devis',
        numero: '',
        montant_ht: 0,
        tva: 20,
        statut: 'brouillon',
        date_emission: format(new Date(), 'yyyy-MM-dd'),
        date_echeance: '',
        notes: ''
      });
    }
    setNumeroValide(true);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!numeroValide) { toast.error('Ce numéro est déjà utilisé par un autre document'); return; }
    try {
      if (editingDocument) {
        await axios.put(`${API_URL}/documents/${editingDocument.id}`, formData);
        toast.success('Document mis à jour');
      } else {
        await axios.post(`${API_URL}/documents`, formData);
        toast.success('Document créé');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/documents/${deleteId}`);
      setDocuments(documents.filter(d => d.id !== deleteId));
      toast.success('Document supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteId(null);
    }
  };

  const handleStatusChange = async (documentId, newStatus) => {
    try {
      await axios.put(`${API_URL}/documents/${documentId}/status?statut=${newStatus}`);
      fetchData();
      toast.success('Statut mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleGeneratePDF = (doc) => downloadPdf(`${API_URL}/documents/${doc.id}/pdf`);
  // Prévisualisation du PDF dans un nouvel onglet (sans téléchargement)
  const previewPdf = async (doc) => {
    try {
      const res = await axios.get(`${API_URL}/documents/${doc.id}/pdf`, { responseType: 'blob' });
      window.open(URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })), '_blank', 'noopener');
    } catch { toast.error('Aperçu indisponible'); }
  };

  const getDossierName = (dossierId) => {
    const dossier = dossiers.find(d => d.id === dossierId);
    return dossier ? `${dossier.code} - ${dossier.intitule}` : 'Dossier inconnu';
  };

  const getDossierDisplay = (doc) => {
    // Use enriched data from /documents/all (dossiers AF/AFT et prestations)
    if (doc.dossier_code) return `${doc.dossier_code} — ${doc.dossier_intitule || ''}`;
    if (doc.dossier_intitule) return doc.dossier_intitule;
    return getDossierName(doc.dossier_id);
  };
  // Raccourci vers la fiche du dossier (AF/AFT) ou de la prestation
  const lienDossier = (doc) => doc.dossier_type === 'prestation' ? `/prestations/${doc.dossier_id}` : `/dossiers/${doc.dossier_id}`;
  const destinataires = [...new Set(documents.map(d => d.destinataire_nom).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'fr'));

  const calculateTTC = (ht, tva) => {
    return (ht * (1 + tva / 100)).toFixed(2);
  };

  const filteredDocuments = trierDocuments(documents.filter(doc => {
    const q = searchTerm.toLowerCase();
    const matchesSearch = !q || [doc.numero, getDossierDisplay(doc), doc.destinataire_nom, doc.code_action_client, ...(doc.references_bdc || [])].filter(Boolean).join(' ').toLowerCase().includes(q);
    const matchesType = filterType === 'all' || doc.type_document === filterType;
    const matchesStatut = filterStatut === 'all' || doc.statut === filterStatut;
    const matchesDest = filterDestinataire === 'all' || doc.destinataire_nom === filterDestinataire;
    return matchesSearch && matchesType && matchesStatut && matchesDest && estDansPeriode(doc, periode);
  }), tri, getDossierDisplay);

  const filtresActifs = searchTerm || filterType !== 'all' || filterStatut !== 'all' || filterDestinataire !== 'all' || periode.du || periode.au;
  const nomArchive = [
    filterType !== 'all' ? filterType + 's' : 'documents',
    filterDestinataire !== 'all' ? filterDestinataire : null,
    periode.du || periode.au ? `${periode.du || 'debut'}_${periode.au || 'fin'}` : null,
  ].filter(Boolean).join('_');

  // Stats
  const stats = {
    devis: documents.filter(d => d.type_document === 'devis').length,
    conventions: documents.filter(d => d.type_document === 'convention').length,
    factures: documents.filter(d => d.type_document === 'facture').length,
    totalHT: documents.filter(d => d.type_document === 'facture' && d.statut !== 'annule')
                      .reduce((sum, d) => sum + d.montant_ht, 0)
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="facturation-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Facturation</h1>
            <p className="text-slate-500 mt-1">Gestion des devis, conventions et factures</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => navigate('/facturation/chaine')} data-testid="facturation-chaine-btn">
              <Receipt className="w-4 h-4 mr-2" />
              Facturation à la chaîne
            </Button>
            <Button onClick={() => handleOpenDialog()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-document-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau document
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Devis</p>
              <p className="text-2xl font-bold text-slate-900">{stats.devis}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Conventions</p>
              <p className="text-2xl font-bold text-slate-900">{stats.conventions}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Factures</p>
              <p className="text-2xl font-bold text-slate-900">{stats.factures}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-emerald-50">
            <CardContent className="p-4">
              <p className="text-sm text-emerald-600">Total HT Facturé</p>
              <p className="text-2xl font-bold text-emerald-700">{stats.totalHT.toLocaleString()} €</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-documents"
            />
          </div>
          <Tabs value={filterType} onValueChange={setFilterType}>
            <TabsList className="bg-slate-100">
              <TabsTrigger value="all">Tous</TabsTrigger>
              <TabsTrigger value="devis">Devis</TabsTrigger>
              <TabsTrigger value="convention">Conventions</TabsTrigger>
              <TabsTrigger value="facture">Factures</TabsTrigger>
            </TabsList>
          </Tabs>
          <Select value={filterStatut} onValueChange={setFilterStatut}>
            <SelectTrigger className="w-40" data-testid="factu-filtre-statut"><SelectValue placeholder="Statut" /></SelectTrigger>
            <SelectContent>{STATUTS_DOC.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterDestinataire} onValueChange={setFilterDestinataire}>
            <SelectTrigger className="w-56" data-testid="factu-filtre-destinataire"><SelectValue placeholder="Destinataire" /></SelectTrigger>
            <SelectContent className="max-h-72"><SelectItem value="all">Tous destinataires</SelectItem>{destinataires.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <FiltrePeriode periode={periode} onChange={setPeriode} />
          <div className="sm:ml-auto flex items-center gap-3">
            <span className="text-sm text-slate-500" data-testid="factu-nb-resultats">{filteredDocuments.length} document{filteredDocuments.length > 1 ? 's' : ''}{filtresActifs ? ' (filtrés)' : ''}</span>
            <BoutonTelechargerZip documents={filteredDocuments} nomArchive={nomArchive} />
          </div>
        </div>

        {/* Documents Table */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Chargement...</div>
            ) : filteredDocuments.length === 0 ? (
              <div className="p-8 text-center">
                <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-1">Aucun document</h3>
                <p className="text-slate-500 mb-4">
                  {filtresActifs ? 'Aucun résultat pour ces filtres' : 'Créez votre premier document'}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TriHead col="numero" tri={tri} onTri={(c) => setTri(t => cycleTri(t, c))}>Numéro</TriHead>
                    <TriHead col="type" tri={tri} onTri={(c) => setTri(t => cycleTri(t, c))}>Type</TriHead>
                    <TriHead col="dossier" tri={tri} onTri={(c) => setTri(t => cycleTri(t, c))}>Dossier</TriHead>
                    <TriHead col="destinataire" tri={tri} onTri={(c) => setTri(t => cycleTri(t, c))}>Destinataire</TriHead>
                    <TriHead col="bdc" tri={tri} onTri={(c) => setTri(t => cycleTri(t, c))}>Réf. BDC</TriHead>
                    <TriHead col="montant_ht" tri={tri} onTri={(c) => setTri(t => cycleTri(t, c))}>Montant HT</TriHead>
                    <TriHead col="montant_ttc" tri={tri} onTri={(c) => setTri(t => cycleTri(t, c))}>TTC</TriHead>
                    <TriHead col="date" tri={tri} onTri={(c) => setTri(t => cycleTri(t, c))}>Date</TriHead>
                    <TriHead col="statut" tri={tri} onTri={(c) => setTri(t => cycleTri(t, c))}>Statut</TriHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDocuments.map((doc) => (
                    <TableRow key={doc.id} className="hover:bg-slate-50" data-testid={`factu-row-${doc.id}`}>
                      <TableCell className="font-mono text-sm text-indigo-600">
                        <span className="inline-flex items-center gap-1.5">
                          <button type="button" onClick={() => previewPdf(doc)} className="text-slate-400 hover:text-indigo-600 transition-colors" title="Prévisualiser le PDF" data-testid={`doc-preview-${doc.id}`}><Eye className="w-4 h-4" /></button>
                          {doc.numero}
                        </span>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={doc.type_document} />
                      </TableCell>
                      <TableCell className="max-w-[200px] text-xs">
                        <button type="button" onClick={() => navigate(lienDossier(doc))} className="group inline-flex items-center gap-1 max-w-full text-left hover:text-indigo-700" title={doc.dossier_type === 'prestation' ? 'Ouvrir la prestation' : 'Ouvrir le dossier'} data-testid={`doc-dossier-link-${doc.id}`}>
                          {doc.dossier_type === 'prestation' && <span className="shrink-0 rounded bg-violet-100 px-1 text-[9px] font-bold text-violet-700">PRESTA</span>}
                          <span className="truncate">{getDossierDisplay(doc)}</span>
                          <ExternalLink className="w-3 h-3 shrink-0 opacity-0 group-hover:opacity-100 text-indigo-500" />
                        </button>
                      </TableCell>
                      <TableCell className="text-xs">
                        {doc.destinataire_nom || '—'}
                        {doc.code_action_client && <span className="ml-1 text-[10px] text-purple-600 font-mono">[{doc.code_action_client}]</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(doc.references_bdc || []).map((ref, i) => (
                            <span key={i} className="text-[10px] font-mono bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">{ref}</span>
                          ))}
                          {(!doc.references_bdc || doc.references_bdc.length === 0) && <span className="text-[10px] text-slate-400">—</span>}
                        </div>
                      </TableCell>
                      <TableCell>{(doc.montant_ht || 0).toLocaleString()} €</TableCell>
                      <TableCell className="font-medium">{(doc.montant_ttc || doc.montant_ht || 0).toLocaleString()} €</TableCell>
                      <TableCell>
                        {doc.date_emission ? format(parseISO(doc.date_emission), 'dd/MM/yyyy') : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <StatusBadge status={doc.statut} />
                          {['facture', 'avoir'].includes(doc.type_document) && <FactureVerrouActions doc={doc} onEdit={setEditingFacture} onChanged={fetchData} size="w-3.5 h-3.5" />}
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`doc-menu-${doc.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {['facture', 'avoir'].includes(doc.type_document) ? (
                              <DropdownMenuItem disabled={isFactureLocked(doc)} onClick={() => setEditingFacture(doc)} data-testid={`doc-edit-${doc.id}`}>
                                {isFactureLocked(doc) ? <Lock className="w-4 h-4 mr-2" /> : <Pencil className="w-4 h-4 mr-2" />}
                                {isFactureLocked(doc) ? 'Verrouillée (déverrouiller via le cadenas)' : 'Modifier'}
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => handleOpenDialog(doc)} data-testid={`doc-edit-${doc.id}`}>
                                <Pencil className="w-4 h-4 mr-2" />
                                Modifier
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => previewPdf(doc)} data-testid={`doc-preview-menu-${doc.id}`}>
                              <Eye className="w-4 h-4 mr-2" />
                              Prévisualiser
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleGeneratePDF(doc)} data-testid={`doc-download-${doc.id}`}>
                              <Download className="w-4 h-4 mr-2" />
                              Télécharger PDF
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {doc.statut === 'brouillon' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(doc.id, 'envoye')}>
                                <Send className="w-4 h-4 mr-2" />
                                Marquer envoyé
                              </DropdownMenuItem>
                            )}
                            {doc.statut === 'envoye' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(doc.id, 'signe')}>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Marquer signé
                              </DropdownMenuItem>
                            )}
                            {doc.type_document === 'facture' && doc.statut === 'signe' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(doc.id, 'paye')}>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Marquer payé
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => setDeleteId(doc.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <FactureEditDialog doc={editingFacture} open={!!editingFacture} onClose={() => setEditingFacture(null)} onSaved={fetchData} />

        {/* Document Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Plus_Jakarta_Sans']">
                {editingDocument ? 'Modifier le document' : 'Nouveau document'}
              </DialogTitle>
              <DialogDescription>
                {editingDocument ? 'Modifiez les informations du document' : 'Créez un nouveau document'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="flex justify-end -mt-2">
                <Button type="submit" size="sm" className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-document-top">
                  {editingDocument ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="type_document">Type de document *</Label>
                  <Select
                    value={formData.type_document}
                    onValueChange={(value) => setFormData({ ...formData, type_document: value })}
                  >
                    <SelectTrigger data-testid="select-type-document">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DOCUMENT_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dossier_id">Dossier *</Label>
                  <Select
                    value={formData.dossier_id}
                    onValueChange={(value) => setFormData({ ...formData, dossier_id: value })}
                  >
                    <SelectTrigger data-testid="select-dossier">
                      <SelectValue placeholder="Sélectionner un dossier" />
                    </SelectTrigger>
                    <SelectContent>
                      {dossiers.map((dossier) => (
                        <SelectItem key={dossier.id} value={dossier.id}>
                          {dossier.code} - {dossier.intitule}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <NumeroDocumentField value={formData.numero} onChange={(v) => setFormData(f => ({ ...f, numero: v }))} typeDocument={formData.type_document} excludeId={editingDocument?.id} onValidite={setNumeroValide} />

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="montant_ht">Montant HT (€) *</Label>
                  <Input
                    id="montant_ht"
                    type="number"
                    step="0.01"
                    value={formData.montant_ht}
                    onChange={(e) => setFormData({ ...formData, montant_ht: parseFloat(e.target.value) || 0 })}
                    required
                    data-testid="input-montant-ht"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tva">TVA (%)</Label>
                  <Input
                    id="tva"
                    type="number"
                    step="0.1"
                    value={formData.tva}
                    onChange={(e) => setFormData({ ...formData, tva: parseFloat(e.target.value) || 0 })}
                    data-testid="input-tva"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Montant TTC</Label>
                  <Input
                    value={`${calculateTTC(formData.montant_ht, formData.tva)} €`}
                    disabled
                    className="bg-slate-50"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date_emission">Date d'émission</Label>
                  <Input
                    id="date_emission"
                    type="date"
                    value={formData.date_emission}
                    onChange={(e) => setFormData({ ...formData, date_emission: e.target.value })}
                    data-testid="input-date-emission"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date_echeance">Date d'échéance</Label>
                  <Input
                    id="date_echeance"
                    type="date"
                    value={formData.date_echeance}
                    onChange={(e) => setFormData({ ...formData, date_echeance: e.target.value })}
                    data-testid="input-date-echeance"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="statut">Statut</Label>
                <Select
                  value={formData.statut}
                  onValueChange={(value) => setFormData({ ...formData, statut: value })}
                >
                  <SelectTrigger data-testid="select-statut">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUTS.map((statut) => (
                      <SelectItem key={statut.value} value={statut.value}>
                        {statut.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notes ou commentaires..."
                  rows={3}
                  data-testid="input-notes"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-document">
                  {editingDocument ? 'Mettre à jour' : 'Créer'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer ce document ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};
